import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './styles/tokens.css';
import './styles/global.css';

import { ToastProvider } from './organisms';
import { App } from './App';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { GenericListByConfig } from './pages/GenericList';
import { GenericDetailByConfig } from './pages/GenericDetail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function AccountsList() {
  return <GenericListByConfig table="accounts" title="Accounts" routePath="accounts" apiPath="accounts" />;
}
function AccountDetail() {
  return <GenericDetailByConfig table="accounts" title="Accounts" apiPath="accounts" routePath="accounts" />;
}

function ContactsList() {
  return <GenericListByConfig table="contacts" title="Contacts" routePath="contacts" apiPath="contacts" />;
}
function ContactDetail() {
  return <GenericDetailByConfig table="contacts" title="Contacts" apiPath="contacts" routePath="contacts" />;
}

function RelationshipsList() {
  return <GenericListByConfig table="relationships" title="Relationships" routePath="relationships" apiPath="relationships" />;
}
function RelationshipDetail() {
  return <GenericDetailByConfig table="relationships" title="Relationships" apiPath="relationships" routePath="relationships" />;
}

function WorkOrdersList() {
  return <GenericListByConfig table="work_orders" title="Work Orders" routePath="work-orders" apiPath="work-orders" />;
}
function WorkOrderDetail() {
  return <GenericDetailByConfig table="work_orders" title="Work Orders" apiPath="work-orders" routePath="work-orders" />;
}

function WorkTypesList() {
  return <GenericListByConfig table="work_types" title="Work Types" routePath="work-types" apiPath="work-types" />;
}
function WorkTypeDetail() {
  return <GenericDetailByConfig table="work_types" title="Work Types" apiPath="work-types" routePath="work-types" />;
}

function AssetsList() {
  return <GenericListByConfig table="assets" title="Assets" routePath="assets" apiPath="assets" />;
}
function AssetDetail() {
  return <GenericDetailByConfig table="assets" title="Assets" apiPath="assets" routePath="assets" />;
}

function ServiceResourcesList() {
  return <GenericListByConfig table="service_resources" title="Service Resources" routePath="service-resources" apiPath="service-resources" />;
}
function ServiceResourceDetail() {
  return <GenericDetailByConfig table="service_resources" title="Service Resources" apiPath="service-resources" routePath="service-resources" />;
}

function AuditLogList() {
  return <GenericListByConfig table="audit_logs" title="Audit Log" routePath="audit-log" apiPath="audit-log" disableCreate createCaption="Append-only system log" />;
}

function UsersList() {
  return <GenericListByConfig table="users" title="Users" routePath="users" apiPath="users" disableCreate createCaption="Use the invitation flow" />;
}
function UserDetail() {
  return <GenericDetailByConfig table="users" title="Users" apiPath="users" routePath="users" disableEdit />;
}

function SettingsPage() {
  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Settings</h1>
      <p style={{ marginTop: 'var(--sp-2)', color: 'var(--color-ink-3)' }}>
        Platform configuration settings will be available here.
      </p>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<App />}>
              <Route index element={<Dashboard />} />
              {/* CRM */}
              <Route path="accounts" element={<AccountsList />} />
              <Route path="accounts/:id" element={<AccountDetail />} />
              <Route path="contacts" element={<ContactsList />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              <Route path="relationships" element={<RelationshipsList />} />
              <Route path="relationships/:id" element={<RelationshipDetail />} />
              {/* Operations */}
              <Route path="work-orders" element={<WorkOrdersList />} />
              <Route path="work-orders/:id" element={<WorkOrderDetail />} />
              <Route path="work-types" element={<WorkTypesList />} />
              <Route path="work-types/:id" element={<WorkTypeDetail />} />
              <Route path="assets" element={<AssetsList />} />
              <Route path="assets/:id" element={<AssetDetail />} />
              <Route path="service-resources" element={<ServiceResourcesList />} />
              <Route path="service-resources/:id" element={<ServiceResourceDetail />} />
              {/* Configuration */}
              <Route path="settings" element={<SettingsPage />} />
              {/* System */}
              <Route path="audit-log" element={<AuditLogList />} />
              <Route path="users" element={<UsersList />} />
              <Route path="users/:id" element={<UserDetail />} />
              {/* Fallback */}
              <Route path="*" element={<Dashboard />} />
            </Route>
          </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
